import React, { useState, useMemo } from "react";
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { FiTarget } from "react-icons/fi";
import { COLORS, MONTHLY_TARGET, TABLE_RANGES } from "../myDashboardConstants";
import { scaleTo } from "../utils";
import { SectionCard2 } from "../components/SectionCards";
import { RangeTabs } from "../components/RangeTabs";
import { AnimatedNumber } from "../components/AnimatedNumber";
import { RadialDetailTooltip } from "../components/RadialDetailTooltip";

/* ===== from sections/MonthlyTargetRadial.jsx ===== */
export function MonthlyTargetRadial() {
  const [range, setRange] = useState("This Month");
  const stats = useMemo(() => {
    const target = scaleTo(MONTHLY_TARGET.target, range, "mt-target");
    const achieved = Math.round(
      target * (0.74 + (range === "Today" ? 0.05 : 0)),
    );
    const remaining = Math.max(0, target - achieved);
    const pct = Math.round((achieved / target) * 100);
    return { target, achieved, remaining, pct };
  }, [range]);

  const chartData = useMemo(
    () => [
      {
        name: "Target",
        value: 100,
        amount: stats.target,
        fill: COLORS.primary,
        dotColor: COLORS.primary,
      },
      {
        name: "Achieved",
        value: stats.pct,
        amount: stats.achieved,
        fill: COLORS.success,
        dotColor: COLORS.success,
      },
      {
        name: "Remaining",
        value: Math.max(1, 100 - stats.pct),
        amount: stats.remaining,
        fill: COLORS.warning,
        dotColor: COLORS.warning,
      },
    ],
    [stats],
  );

  return (
    <SectionCard2
      title="Monthly Target"
      subtitle="Progress toward quota"
      icon={<FiTarget size={16} />}
      accent={COLORS.primary}
      action={
        <RangeTabs value={range} onChange={setRange} options={TABLE_RANGES} />
      }
      bodyStyle={{ paddingTop: 2 }}
    >
      <div className="sd-radial-wrap">
        <div style={{ width: "100%", height: 236, position: "relative" }}>
          <ResponsiveContainer>
            <RadialBarChart
              innerRadius="38%"
              outerRadius="100%"
              barSize={16}
              data={chartData}
              startAngle={90}
              endAngle={-270}
            >
              <PolarAngleAxis
                type="number"
                domain={[0, 100]}
                angleAxisId={0}
                tick={false}
              />
              <RadialBar
                background={{ fill: "#EEF1F6" }}
                dataKey="value"
                cornerRadius={20}
                isAnimationActive={false}
              />
              <Tooltip
                content={<RadialDetailTooltip />}
                wrapperStyle={{ pointerEvents: "none" }}
                position={{ y: 0 }}
                allowEscapeViewBox={{ x: true, y: true }}
                isAnimationActive={false}
              />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="sd-radial-center">
            <div className="sd-radial-pct" style={{ fontSize: 30 }}>
              <AnimatedNumber
                value={stats.pct}
                format={(v) => `${Math.round(v)}%`}
              />
            </div>
            <div className="sd-radial-caption">Achieved</div>
          </div>
        </div>

        <div className="sd-target-stats">
          <div className="sd-target-stat">
            <span
              className="sd-target-dot"
              style={{ background: COLORS.primary }}
            />
            <span className="sd-target-label">Target</span>
            <span className="sd-target-value">
              ₹{stats.target.toLocaleString()}
            </span>
          </div>
          <div className="sd-target-stat">
            <span
              className="sd-target-dot"
              style={{ background: COLORS.success }}
            />
            <span className="sd-target-label">Achieved</span>
            <span className="sd-target-value">
              ₹{stats.achieved.toLocaleString()}
            </span>
          </div>
          <div className="sd-target-stat">
            <span
              className="sd-target-dot"
              style={{ background: COLORS.warning }}
            />
            <span className="sd-target-label">Remaining</span>
            <span className="sd-target-value">
              ₹{stats.remaining.toLocaleString()}
            </span>
          </div>
          {stats.pct >= 80 && (
            <div className="sd-achievement-badge">
              On track to exceed target
            </div>
          )}
        </div>
      </div>
    </SectionCard2>
  );
}
